/**
 * 要員の型と入力検証（02 5.3.12 personnel）。
 * - 要員コードは自動採番だけ（13 章 C-28）。本文に personnel_code があれば定義外の項目として 400（strict：D-019）
 * - 紹介元は顧客（企業・個人事業主）の選択。存在しない・削除済みは service が 422 RELATION_001
 * - スキルは各 50 文字・100 個まで。重複は画面で足さない（03 SCR-053 No.13）
 * - ステータスは利用者が選んで切り替える（自動では変えない：要件書 2.3）
 */
import { z } from 'zod';
import { AFFILIATION_TYPE_VALUES } from '../enums/affiliation-type';
import { CUSTOMER_TYPE_VALUES } from '../enums/customer-type';
import { PERSONNEL_STATUS_VALUES } from '../enums/personnel-status';
import { PERSONNEL_TYPE_VALUES } from '../enums/personnel-type';
import { zCode, zEmail, zEnum, zPhone, zRequiredText, zShortText, zUrl, zVersion } from './common.schema';
import { zNullable } from './customer.schema';
import { buildListQuerySchema, CommonAttributesSchema } from './list-query.schema';

/** スキル（各 50 文字・100 個まで：02 5.3.12） */
export const zSkills = z.array(zRequiredText(50)).max(100, 'MSG-V-020');

export const PERSONNEL_INPUT_SHAPE = {
  personnel_type: zEnum(PERSONNEL_TYPE_VALUES),
  personnel_name: zRequiredText(100),
  personnel_name_phonetic: zShortText(100).optional(),
  affiliation_type: zEnum(AFFILIATION_TYPE_VALUES),
  personnel_status: zEnum(PERSONNEL_STATUS_VALUES).optional(),
  referrer_customer_code: zNullable(zCode).optional(),
  email: zNullable(zEmail).optional(),
  phone_number: zNullable(zPhone).optional(),
  contract_document_department: zShortText(100).optional(),
  contract_document_email: zNullable(zEmail).optional(),
  timesheet_email: zNullable(zEmail).optional(),
  skills: zSkills.optional(),
  skill_sheet_url: zNullable(zUrl).optional(),
  note: zShortText(2000).optional(),
};

/** API-PERS-02 要員の登録 */
export const PersonnelCreateSchema = z.object(PERSONNEL_INPUT_SHAPE).strict();

/** API-PERS-04 要員の更新（すべて任意＋version） */
export const PersonnelUpdateSchema = z
  .object({
    ...PERSONNEL_INPUT_SHAPE,
    personnel_type: PERSONNEL_INPUT_SHAPE.personnel_type.optional(),
    personnel_name: PERSONNEL_INPUT_SHAPE.personnel_name.optional(),
    affiliation_type: PERSONNEL_INPUT_SHAPE.affiliation_type.optional(),
    version: zVersion,
  })
  .strict();

const DeletedMarksSchema = z.object({
  deleted_at: z.string().nullable().optional(),
  delete_batch_code: z.string().nullable().optional(),
});

export const PersonnelSchema = z
  .object({
    personnel_code: z.string(),
    personnel_type: z.enum(PERSONNEL_TYPE_VALUES),
    personnel_name: z.string(),
    personnel_name_phonetic: z.string().nullable(),
    affiliation_type: z.enum(AFFILIATION_TYPE_VALUES),
    personnel_status: z.enum(PERSONNEL_STATUS_VALUES),
    referrer_customer_code: z.string().nullable(),
    referrer_customer_name: z.string().nullable(),
    /** リンク先（SCR-007／SCR-015）の判定用（03 BE-03） */
    referrer_customer_type: z.enum(CUSTOMER_TYPE_VALUES).nullable(),
    /** 紹介元が削除済み（02 2.9「<参照先>_deleted」） */
    referrer_customer_deleted: z.boolean(),
    email: z.string().nullable(),
    phone_number: z.string().nullable(),
    contract_document_department: z.string().nullable(),
    contract_document_email: z.string().nullable(),
    timesheet_email: z.string().nullable(),
    skills: z.array(z.string()),
    skill_sheet_url: z.string().nullable(),
    note: z.string().nullable(),
  })
  .merge(CommonAttributesSchema)
  .merge(DeletedMarksSchema);

export const PERSONNEL_LIST_SORT_VALUES = [
  'personnel_code',
  'personnel_name',
  'created_at',
  'personnel_status',
] as const;

/** API-PERS-01（02 2.3.2） */
export const PersonnelListQuerySchema = buildListQuerySchema(PERSONNEL_LIST_SORT_VALUES, 'personnel_code').extend({
  personnel_status: zEnum(PERSONNEL_STATUS_VALUES).optional(),
  affiliation_type: zEnum(AFFILIATION_TYPE_VALUES).optional(),
  personnel_type: zEnum(PERSONNEL_TYPE_VALUES).optional(),
  referrer_customer_code: zCode.optional(),
});

export type PersonnelCreateInput = z.infer<typeof PersonnelCreateSchema>;
export type PersonnelUpdateInput = z.infer<typeof PersonnelUpdateSchema>;
export type Personnel = z.infer<typeof PersonnelSchema>;
export type PersonnelListQuery = z.infer<typeof PersonnelListQuerySchema>;
